<template>
  <div v-if="show" id="intro-cutscene" @click.stop>
    <canvas ref="cutsceneCanvas" id="cutscene-canvas"></canvas>
    
    <!-- Skip button -->
    <button 
      @click.stop="skipCutscene" 
      class="skip-button"
      type="button"
    >
      Overslaan
    </button>
    
    <!-- Text overlays for each scene -->
    <div class="title-overlay" v-show="currentScene === 0">
      <h1 class="scene-title">⚔️ THE ULTIMATE WAR ⚔️</h1>
    </div>
    
    <div class="text-overlay" v-show="currentScene === 1">
      <p class="scene-text">Lang geleden leefden vier rassen in vrede...</p>
    </div>
    
    <div class="text-overlay" v-show="currentScene === 2">
      <p class="scene-text">Maar de vrede was fragiel...</p>
    </div>
    
    <div class="text-overlay" v-show="currentScene === 3">
      <p class="scene-text">Jij bent een krijger van het {{ raceName }} ras</p>
    </div>
    
    <div class="text-overlay" v-show="currentScene === 4">
      <p class="scene-text">De strijd begint nu...</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import * as THREE from 'three';
import { Races } from '../../game/races/Races.js';

const props = defineProps({
  show: { type: Boolean, default: false },
  race: { type: String, default: 'human' }
});

const emit = defineEmits(['close']);

const cutsceneCanvas = ref(null);
const scene = ref(null);
const camera = ref(null);
const renderer = ref(null);
const characters = ref([]);
const currentScene = ref(0);
const animationId = ref(null);
const clock = new THREE.Clock();
const sceneTimers = ref([]);
const sceneStartTime = ref(0);

const raceNames = {
  human: 'Mensen',
  orc: 'Orks',
  elf: 'Elven',
  troll: 'Trollen',
  wizard: 'Wizard'
};

const raceName = computed(() => raceNames[props.race] || 'Mensen');

// Character creation functions (simplified versions from Enemy.js)
function createCharacterMesh(race, color) {
  try {
    const group = new THREE.Group();
    const material = new THREE.MeshStandardMaterial({ 
      color: color,
      metalness: 0.3,
      roughness: 0.7
    });
  
  // Head
  const headGeometry = new THREE.BoxGeometry(0.4, 0.4, 0.4);
  const head = new THREE.Mesh(headGeometry, material);
  head.position.set(0, 1.6, 0);
  group.add(head);
  
  // Body
  const bodyGeometry = new THREE.BoxGeometry(0.5, 0.8, 0.3);
  const body = new THREE.Mesh(bodyGeometry, material);
  body.position.set(0, 1.0, 0);
  group.add(body);
  
  // Arms
  const armGeometry = new THREE.BoxGeometry(0.15, 0.6, 0.15);
  const leftArm = new THREE.Mesh(armGeometry, material);
  leftArm.position.set(-0.35, 0.9, 0);
  group.add(leftArm);
  
  const rightArm = new THREE.Mesh(armGeometry, material);
  rightArm.position.set(0.35, 0.9, 0);
  group.add(rightArm);
  
  // Legs
  const legGeometry = new THREE.BoxGeometry(0.2, 0.6, 0.2);
  const leftLeg = new THREE.Mesh(legGeometry, material);
  leftLeg.position.set(-0.15, 0.3, 0);
  group.add(leftLeg);
  
  const rightLeg = new THREE.Mesh(legGeometry, material);
  rightLeg.position.set(0.15, 0.3, 0);
  group.add(rightLeg);
  
  // Race-specific features
  if (race === 'elf') {
    // Pointed ears
    const earGeometry = new THREE.ConeGeometry(0.08, 0.3, 3);
    const leftEar = new THREE.Mesh(earGeometry, material);
    leftEar.rotation.z = Math.PI / 6;
    leftEar.position.set(-0.25, 1.75, 0);
    group.add(leftEar);
    
    const rightEar = new THREE.Mesh(earGeometry, material);
    rightEar.rotation.z = -Math.PI / 6;
    rightEar.position.set(0.25, 1.75, 0);
    group.add(rightEar);
  } else if (race === 'orc') {
    // Larger head
    head.scale.set(1.2, 1.2, 1.2);
    // Green tint
    material.color.setHex(0x228B22);
  } else if (race === 'troll') {
    // Much larger
    group.scale.set(1.5, 1.5, 1.5);
    material.color.setHex(0x8B4513);
  }
  
  return group;
  } catch (error) {
    console.error('Error creating character mesh:', error);
    // Return empty group on error
    return new THREE.Group();
  }
}

function initCutscene() {
  console.log('Initializing cutscene...');
  if (!cutsceneCanvas.value) {
    console.error('Canvas not found!');
    return;
  }
  
  // Reset scene counter
  currentScene.value = 0;
  
  // Scene setup
  scene.value = new THREE.Scene();
  scene.value.background = new THREE.Color(0x1a1a2e);
  
  // Camera
  camera.value = new THREE.PerspectiveCamera(
    75,
    window.innerWidth / window.innerHeight,
    0.1,
    1000
  );
  camera.value.position.set(0, 3, 8);
  camera.value.lookAt(0, 1, 0);
  
  // Renderer - use separate context
  try {
    renderer.value = new THREE.WebGLRenderer({
      canvas: cutsceneCanvas.value,
      antialias: true,
      alpha: false,
      powerPreference: "high-performance"
    });
    renderer.value.setSize(window.innerWidth, window.innerHeight);
    renderer.value.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.value.setClearColor(0x1a1a2e, 1);
  } catch (error) {
    console.error('Error creating renderer:', error);
    // Skip cutscene if renderer can't be created
    setTimeout(() => closeCutscene(), 100);
    return;
  }
  
  // Lighting
  const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
  scene.value.add(ambientLight);
  
  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
  directionalLight.position.set(5, 10, 5);
  scene.value.add(directionalLight);
  
  // Create characters for all races
  const races = ['human', 'orc', 'elf', 'troll'];
  characters.value = [];
  
  races.forEach((race, index) => {
    try {
      const color = Races[race]?.color || 0xffffff;
      const character = createCharacterMesh(race, color);
      if (character) {
        character.position.set((index - 1.5) * 3, 0, 0);
        scene.value.add(character);
        characters.value.push({ mesh: character, race });
      }
    } catch (error) {
      console.error(`Error creating character for race ${race}:`, error);
    }
  });
  
  console.log('Cutscene initialized, starting animation...');
  
  // Reset scene and start time
  currentScene.value = 0;
  sceneStartTime.value = Date.now();
  
  // Start animation
  animate();
  
  // Animate camera and characters
  animateCutscene();
  
  // Auto-close after animation (backup, but animateCutscene should handle it)
  sceneTimers.value.push(setTimeout(() => {
    if (props.show) {
      closeCutscene();
    }
  }, 7000)); // 7 seconds max (6s animation + 1s buffer)
}

function animateCutscene() {
  const startTime = Date.now();
  const duration = 6000; // 6 seconds in milliseconds
  
  const animate = () => {
    if (!props.show) {
      return; // Stop if cutscene is closed
    }
    
    const elapsed = Date.now() - startTime; // elapsed in milliseconds
    const elapsedSeconds = elapsed / 1000; // elapsed in seconds for animations
    const progress = Math.min(elapsed / duration, 1); // progress 0-1
    
    if (progress >= 1) {
      // Animation complete, close cutscene
      closeCutscene();
      return;
    }
    
    try {
      // Scene 0: Show all characters, camera pans around (first 1.5 seconds)
      if (progress < 0.25) {
        currentScene.value = 0;
        // Camera circles around characters
        const angle = (progress / 0.25) * Math.PI * 2;
        const radius = 10;
        if (camera.value) {
          camera.value.position.set(
            Math.sin(angle) * radius,
            5,
            Math.cos(angle) * radius
          );
          camera.value.lookAt(0, 1, 0);
        }
      }
      // Scene 1: Characters move towards center (battle formation) - 1.5-3 seconds
      else if (progress < 0.5) {
        currentScene.value = 1;
        const moveProgress = (progress - 0.25) / 0.25;
        characters.value.forEach((char, index) => {
          if (char && char.mesh) {
            const startX = (index - 1.5) * 3;
            const targetX = (index - 1.5) * 2;
            char.mesh.position.x = startX + (targetX - startX) * moveProgress;
            char.mesh.rotation.y = Math.sin(elapsedSeconds * 2) * 0.3;
          }
        });
        // Camera moves closer
        if (camera.value) {
          camera.value.position.set(0, 4, 6);
          camera.value.lookAt(0, 1, 0);
        }
      }
      // Scene 2: Characters face each other (confrontation) - 3-4.5 seconds
      else if (progress < 0.75) {
        currentScene.value = 2;
        const faceProgress = (progress - 0.5) / 0.25;
        characters.value.forEach((char, index) => {
          if (char && char.mesh) {
            // Face center
            const targetRotation = index < 2 ? Math.PI / 2 : -Math.PI / 2;
            char.mesh.rotation.y = targetRotation * faceProgress;
            // Slight bounce animation
            char.mesh.position.y = Math.sin(elapsedSeconds * 3) * 0.1;
          }
        });
      }
      // Scene 3: Focus on player's race - 4.5-5.5 seconds
      else if (progress < 0.92) {
        currentScene.value = 3;
        const playerRaceIndex = ['human', 'orc', 'elf', 'troll'].indexOf(props.race);
        if (playerRaceIndex !== -1 && characters.value[playerRaceIndex] && camera.value) {
          const playerChar = characters.value[playerRaceIndex];
          const focusProgress = (progress - 0.75) / 0.17;
          const startPos = { x: 0, y: 4, z: 6 };
          const endPos = {
            x: (playerRaceIndex - 1.5) * 2,
            y: 3,
            z: 4
          };
          camera.value.position.set(
            startPos.x + (endPos.x - startPos.x) * focusProgress,
            startPos.y + (endPos.y - startPos.y) * focusProgress,
            startPos.z + (endPos.z - startPos.z) * focusProgress
          );
          camera.value.lookAt(playerChar.mesh.position);
          
          // Player character does action (swing weapon)
          playerChar.mesh.rotation.z = Math.sin(elapsedSeconds * 5) * 0.2;
        }
      }
      // Scene 4: All characters ready for battle - final 0.5 seconds
      else {
        currentScene.value = 4;
        // All characters in battle pose
        characters.value.forEach((char) => {
          if (char && char.mesh) {
            char.mesh.rotation.y += 0.01;
            char.mesh.position.y = 0.5 + Math.sin(elapsedSeconds * 2) * 0.1;
          }
        });
        // Camera pulls back
        if (camera.value) {
          camera.value.position.set(0, 5, 8);
          camera.value.lookAt(0, 1, 0);
        }
      }
      
      // Continue animation if not complete and cutscene is still showing
      if (props.show && progress < 1) {
        requestAnimationFrame(animate);
      } else if (progress >= 1 && props.show) {
        // Animation complete, close cutscene
        closeCutscene();
      }
    } catch (error) {
      console.error('Error in cutscene animation:', error);
      // Close cutscene on error
      if (props.show) {
        closeCutscene();
      }
    }
  };
  
  animate();
}

function animateCharacter(mesh, targetX, targetZ, duration) {
  const startX = mesh.position.x;
  const startZ = mesh.position.z;
  const startTime = Date.now();
  
  const animate = () => {
    const elapsed = (Date.now() - startTime) / 1000;
    const progress = Math.min(elapsed / duration, 1);
    const easeProgress = progress * (2 - progress); // Ease out
    
    mesh.position.x = startX + (targetX - startX) * easeProgress;
    mesh.position.z = startZ + (targetZ - startZ) * easeProgress;
    
    // Rotate character
    mesh.rotation.y = Math.sin(elapsed * 2) * 0.2;
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };
  animate();
}

function animate() {
  if (!animationId.value || !props.show) return; // Already stopped or cutscene closed
  
  animationId.value = requestAnimationFrame(animate);
  
  try {
    if (!scene.value || !camera.value || !renderer.value || !props.show) {
      return;
    }
    
    const deltaTime = Math.min(clock.getDelta(), 0.1); // Cap delta time
    
    // Rotate characters slowly
    characters.value.forEach(char => {
      if (char && char.mesh && char.mesh.rotation) {
        char.mesh.rotation.y += deltaTime * 0.5;
      }
    });
    
    // Only render if everything is valid
    if (renderer.value && scene.value && camera.value) {
      renderer.value.render(scene.value, camera.value);
    }
  } catch (error) {
    console.error('Error in animate loop:', error);
    // Stop animation on error
    if (animationId.value) {
      cancelAnimationFrame(animationId.value);
      animationId.value = null;
    }
    // Close cutscene on persistent error
    setTimeout(() => closeCutscene(), 100);
  }
}

function skipCutscene() {
  closeCutscene();
}

function closeCutscene() {
  console.log('Closing cutscene...');
  
  // Stop animation first
  if (animationId.value) {
    try {
      cancelAnimationFrame(animationId.value);
    } catch (e) {
      // Ignore
    }
    animationId.value = null;
  }
  
  // Clear all timers (both setTimeout and setInterval)
  sceneTimers.value.forEach(timer => {
    if (timer) {
      try {
        clearTimeout(timer);
        clearInterval(timer);
      } catch (e) {
        // Ignore
      }
    }
  });
  sceneTimers.value = [];
  
  // Dispose renderer
  if (renderer.value) {
    try {
      // Clear the canvas
      const ctx = cutsceneCanvas.value?.getContext('webgl2') || cutsceneCanvas.value?.getContext('webgl');
      if (ctx) {
        const loseContext = ctx.getExtension('WEBGL_lose_context');
        if (loseContext) {
          loseContext.loseContext();
        }
      }
      renderer.value.dispose();
      // Try to force context loss if available
      if (renderer.value.forceContextLoss) {
        renderer.value.forceContextLoss();
      }
    } catch (e) {
      console.warn('Error disposing renderer:', e);
    }
    renderer.value = null;
  }
  
  // Dispose scene and geometries
  if (scene.value) {
    try {
      scene.value.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(mat => {
              if (mat && typeof mat.dispose === 'function') {
                try {
                  mat.dispose();
                } catch (e) {
                  // Ignore disposal errors
                }
              }
            });
          } else {
            if (object.material && typeof object.material.dispose === 'function') {
              try {
                object.material.dispose();
              } catch (e) {
                // Ignore disposal errors
              }
            }
          }
        }
      });
      // Clear scene
      while (scene.value.children.length > 0) {
        scene.value.remove(scene.value.children[0]);
      }
    } catch (e) {
      console.warn('Error disposing scene:', e);
    }
    scene.value = null;
  }
  
  // Clear references
  characters.value = [];
  currentScene.value = 0;
  sceneStartTime.value = 0;
  camera.value = null;
  
  // Wait a bit before emitting to ensure cleanup is complete
  setTimeout(() => {
    emit('close');
  }, 100);
}

watch(() => props.show, (newVal) => {
  console.log('Cutscene show prop changed:', newVal);
  if (newVal) {
    // Wait for canvas to be available
    setTimeout(() => {
      if (cutsceneCanvas.value) {
        initCutscene();
      } else {
        console.error('Canvas still not available after timeout');
      }
    }, 200);
  } else {
    closeCutscene();
  }
});

onMounted(() => {
  console.log('Cutscene component mounted, show:', props.show);
  if (props.show) {
    setTimeout(() => {
      if (cutsceneCanvas.value) {
        initCutscene();
      } else {
        console.error('Canvas not available on mount');
      }
    }, 200);
  }
});

onUnmounted(() => {
  closeCutscene();
});
</script>

<style scoped>
#intro-cutscene {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #000;
  z-index: 250;
  pointer-events: all;
}

#cutscene-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.skip-button {
  position: absolute;
  bottom: 30px;
  right: 30px;
  padding: 15px 30px;
  background: rgba(139, 0, 0, 0.8);
  border: 2px solid #A00000;
  color: #fff;
  cursor: pointer;
  border-radius: 8px;
  font-size: 1.1em;
  font-weight: bold;
  transition: all 0.3s;
  z-index: 10;
}

.skip-button:hover {
  background: rgba(160, 0, 0, 0.9);
  transform: scale(1.05);
  box-shadow: 0 0 20px rgba(139, 0, 0, 0.7);
}

.title-overlay,
.text-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  z-index: 5;
}

.scene-title {
  color: #ffaa00;
  font-size: 4em;
  margin: 0;
  text-shadow: 
    0 0 20px rgba(255, 170, 0, 0.8),
    0 0 40px rgba(255, 170, 0, 0.6);
  animation: titleFade 2s ease-in-out;
}

.scene-text {
  color: #fff;
  font-size: 2em;
  margin: 0;
  text-shadow: 
    2px 2px 4px rgba(0, 0, 0, 0.8),
    0 0 20px rgba(255, 255, 255, 0.3);
  animation: textFade 1.5s ease-in-out;
}

@keyframes textFade {
  0% { opacity: 0; transform: translateY(20px); }
  20% { opacity: 1; transform: translateY(0); }
  80% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-20px); }
}

@keyframes titleFade {
  0% { opacity: 0; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.1); }
}

@media (max-width: 768px) {
  .scene-title {
    font-size: 2.5em;
  }
  
  .scene-text {
    font-size: 1.5em;
  }
  
  .skip-button {
    bottom: 20px;
    right: 20px;
    padding: 12px 24px;
    font-size: 1em;
  }
}
</style>

